'use client'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'
import { useMutation } from '@tanstack/react-query'
import { Brain, Database, Loader2, RefreshCw, Send, Target } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { currentContentLinkContext, localizeContentHref } from '@/pages/content-link-utils'

export function ContactSection() {
  const [complete, setComplete] = useState(false)
  const successRef = useRef<HTMLDivElement>(null)
  const { t } = useI18n()
  const contentLinkContext = currentContentLinkContext()
  const trpc = useTRPC()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')

  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (complete)
      successRef.current?.focus()
  }, [complete])

  const visionLayers = [
    {
      icon: Target,
      title: t('ourVision.vision.layer1.title'),
      description: t('ourVision.vision.layer1.description'),
      slug: 'why-werewolf',
    },
    {
      icon: Database,
      title: t('ourVision.vision.layer2.title'),
      description: t('ourVision.vision.layer2.description'),
      slug: 'model-autophagy-disorder',
    },
    {
      icon: RefreshCw,
      title: t('ourVision.vision.layer3.title'),
      description: t('ourVision.vision.layer3.description'),
      slug: 'model-autophagy-disorder',
    },
    {
      icon: Brain,
      title: t('ourVision.vision.layer4.title'),
      description: t('ourVision.vision.layer4.description'),
      slug: 'four-vision',
    },
  ]

  const { mutateAsync: submitContactAsync, isPending: isLoading } = useMutation(
    trpc.contactRouter.submitContact.mutationOptions(),
  )

  const validateEmail = (email: string) => {
    const emailRegex = /^[\w.!#$%&'*+/=?^`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/i
    return emailRegex.test(email) && email.length <= 254
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Record<string, string> = {}

    if (!firstName.trim())
      newErrors.firstName = t('contact.nameRequired')
    if (!lastName.trim())
      newErrors.lastName = t('contact.nameRequired')
    if (!email.trim())
      newErrors.email = t('contact.emailRequired')
    else if (!validateEmail(email))
      newErrors.email = t('contact.emailInvalid')
    if (!message.trim())
      newErrors.message = t('contact.messageRequired')
    else if (message.trim().length < 10)
      newErrors.message = t('contact.messageTooShort')

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})

    try {
      const optionalDetails = [
        companyName.trim() && `Company: ${companyName.trim()}`,
        jobTitle.trim() && `Job: ${jobTitle.trim()}`,
      ].filter(Boolean)
      const result = await submitContactAsync({
        name: `${firstName} ${lastName}`,
        email: email.trim(),
        message: `${optionalDetails.length ? `${optionalDetails.join('\n')}\n\n` : ''}Project Detail: ${message.trim()}`,
      })

      if (!result.success) {
        throw new Error(result.message || 'Failed to submit')
      }
      setComplete(true)
      setFirstName('')
      setLastName('')
      setCompanyName('')
      setJobTitle('')
      setEmail('')
      setMessage('')
    }
    catch (error) {
      console.error('Failed to send message:', error)
      newErrors.submit = t('contact.submitError')
      setErrors(newErrors)
    }
  }

  const handleReset = () => {
    setComplete(false)
    setFirstName('')
    setLastName('')
    setCompanyName('')
    setJobTitle('')
    setEmail('')
    setMessage('')
    setErrors({})
  }

  function renderTextWithBold(text: string) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g)
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>
      }
      return part
    })
  }

  return (
    <div className="min-h-dvh bg-section-dark px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-20">

          { }
          <div className="flex flex-col justify-center">
            <div className="relative mb-12 rounded-2xl border border-primary/10 bg-card/50 p-6 backdrop-blur-xl md:p-10">
              <div className="mb-8">
                <h2 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
                  {t('contact.title')}
                </h2>
              </div>
              <form onSubmit={handleSubmit} noValidate hidden={complete} className="space-y-8" aria-busy={isLoading}>
                { }
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-3">
                    <Label htmlFor="first-name" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {t('contact.firstName')}
                    </Label>
                    <Input
                      id="first-name"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      disabled={isLoading}
                      aria-invalid={Boolean(errors.firstName)}
                      aria-describedby={errors.firstName ? 'first-name-error' : undefined}
                      className={errors.firstName ? 'border-red-500' : ''}
                    />
                    {errors.firstName && <p id="first-name-error" role="alert" className="text-xs text-red-500">{errors.firstName}</p>}
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="last-name" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {t('contact.lastName')}
                    </Label>
                    <Input
                      id="last-name"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      disabled={isLoading}
                      aria-invalid={Boolean(errors.lastName)}
                      aria-describedby={errors.lastName ? 'last-name-error' : undefined}
                      className={errors.lastName ? 'border-red-500' : ''}
                    />
                    {errors.lastName && <p id="last-name-error" role="alert" className="text-xs text-red-500">{errors.lastName}</p>}
                  </div>
                </div>

                { }
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-3">
                    <Label htmlFor="company-name" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {t('contact.companyName')}
                    </Label>
                    <Input
                      id="company-name"
                      value={companyName}
                      onChange={e => setCompanyName(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="job-title" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {t('contact.jobTitle')}
                    </Label>
                    <Input
                      id="job-title"
                      value={jobTitle}
                      onChange={e => setJobTitle(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                { }
                <div className="space-y-3">
                  <Label htmlFor="work-email" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    {t('contact.email')}
                  </Label>
                  <Input
                    id="work-email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    disabled={isLoading}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? 'work-email-error' : undefined}
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && <p id="work-email-error" role="alert" className="text-xs text-red-500">{errors.email}</p>}
                </div>

                { }
                <div className="space-y-3">
                  <Label htmlFor="project-detail" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    {t('contact.message')}
                  </Label>
                  <Textarea
                    id="project-detail"
                    rows={4}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    disabled={isLoading}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? 'project-detail-error' : undefined}
                    className={errors.message ? 'border-red-500' : ''}
                  />
                  {errors.message && <p id="project-detail-error" role="alert" className="text-xs text-red-500">{errors.message}</p>}
                </div>

                <div className="space-y-8">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    size="lg"
                    className="w-full font-bold"
                  >
                    {isLoading ? (
                      <Loader2 className="mr-2 size-5 animate-spin" />
                    ) : (
                      <Send className="mr-2 size-5" />
                    )}
                    {isLoading ? t('contact.sending') : t('contact.send')}
                  </Button>

                  <p className="text-xs leading-relaxed text-muted-foreground">
                    {t('contact.privacyPolicy')}
                  </p>
                </div>

                {errors.submit && <p role="alert" className="text-center text-sm text-red-500">{errors.submit}</p>}
              </form>
              {complete && (
                <div
                  ref={successRef}
                  role="status"
                  aria-live="polite"
                  tabIndex={-1}
                  className="rounded-xl border border-green-500/20 bg-green-500/10 p-6 text-center focus:outline-none"
                >
                  <p className="mb-4 text-sm font-medium text-green-600 dark:text-green-400">
                    {t('contact.successMessage')}
                  </p>
                  <Button type="button" variant="outline" size="sm" onClick={handleReset}>
                    {t('contact.sendAnother')}
                  </Button>
                </div>
              )}
            </div>

          </div>

          { }
          <div className="flex flex-col items-start justify-start lg:mt-0">
            <div className="relative w-full rounded-2xl border border-primary/10 bg-card/50 p-6 backdrop-blur-xl md:p-10">
              <div className="absolute left-0 -top-10 -z-10 size-32 rounded-full bg-primary/20 blur-3xl 2xl:-left-10" />
              <div className="absolute -bottom-10 right-0 -z-10 size-40 rounded-full bg-primary/10 blur-3xl 2xl:-right-10" />

              <div className="mb-10">
                <h3 className="text-xl font-bold tracking-tight text-foreground md:text-2xl">
                  {t('ourVision.title')}
                </h3>
              </div>

              <div className="space-y-10">
                {visionLayers.map((layer, index) => {
                  const IconComponent = layer.icon
                  const href = localizeContentHref(
                    `/blogs/${layer.slug}`,
                    contentLinkContext,
                  ) ?? `/blogs/${layer.slug}`

                  return (
                    <a
                      key={index}
                      href={href}
                      className="group flex gap-6 transition-transform hover:translate-x-1"
                    >
                      <div className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-primary/5 text-primary shadow-sm ring-1 ring-primary/10 transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground group-hover:shadow-primary/20">
                        <IconComponent className="size-6" />
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-lg font-bold text-foreground transition-colors group-hover:text-primary">
                          {layer.title}
                        </h4>
                        <p className="text-sm leading-relaxed text-muted-foreground/90">
                          {renderTextWithBold(layer.description)}
                        </p>
                      </div>
                    </a>
                  )
                })}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
